/**
 * Outgoing Message Pipeline
 *
 * Executes hooks in sequence for messages being sent to server.
 * Fires observer events for logging/observability.
 * Typical hooks: encrypt, sign, log, retry on failure.
 */

import type { IHook, IPipeline, IPipelineContext, PipelineResult } from '../types';
import type { IPipelineObserver } from '../observability/IPipelineObserver';
import { createModuleLogger, deepClone } from '../utils';
import { DiffEngine } from '../utils/DiffEngine';
import { PrePipelineHook } from '../hooks/PrePipelineHook';
import { PostPipelineHook } from '../hooks/PostPipelineHook';

const logger = createModuleLogger('OutgoingPipeline');

export class OutgoingPipeline implements IPipeline {
  readonly phase = 'outgoing' as const;

  readonly hooks: ReadonlyArray<IHook>;

  private observers: IPipelineObserver[] = [];

  constructor(hooks: IHook[] = []) {
    // Register standard hooks first
    const allHooks = [new PrePipelineHook(), ...hooks, new PostPipelineHook()];
    // Sort by priority (lower = runs first)
    this.hooks = allHooks.sort((a, b) => a.priority - b.priority);
  }

  /**
   * Add an observer to receive pipeline events
   */
  addObserver(observer: IPipelineObserver): void {
    this.observers.push(observer);
  }

  /**
   * Remove an observer
   */
  removeObserver(observer: IPipelineObserver): void {
    this.observers = this.observers.filter((o) => o !== observer);
  }

  /**
   * Execute the pipeline with optional observability.
   *
   * @param context Pipeline context with message data
   * @param messageId Optional message ID for observability events
   */
  async execute<TBody = unknown>(
    context: IPipelineContext<TBody>,
    messageId?: string,
  ): Promise<PipelineResult> {
    const executedHooks: string[] = [];
    let lastError: Error | undefined;
    const pipelineStart = Date.now();

    try {
      // Fire pipeline start event
      if (messageId) {
        this.fireEvent('onPipelineStart', {
          messageId,
          serverId: context.serverId,
          topic: context.envelope.meta.topic || '',
          messageType: context.envelope.meta.type || 'unknown',
          protocol: context.envelope.meta.protocol || 'unknown',
          transport: context.envelope.meta.transport || 'unknown',
          direction: context.envelope.direction,
          pipelineName: 'OutgoingPipeline',
          hookSequence: Array.from(this.hooks).map((h) => h.name),
          timestamp: new Date().toISOString(),
          envelopeSnapshot: deepClone(context.envelope),
        });
      }

      for (const hook of this.hooks) {
        if (context.isAborted) {
          logger.debug(`Pipeline aborted at hook ${hook.name}`);
          break;
        }

        const hookStart = Date.now();
        const beforeSnapshot = deepClone(context.envelope);

        // Fire hook before event
        if (messageId) {
          this.fireEvent('onHookBefore', {
            messageId,
            hookName: hook.name,
            priority: hook.priority,
            timestamp: new Date().toISOString(),
            envelopeSnapshot: beforeSnapshot,
          });
        }

        try {
          logger.debug(`Executing hook ${hook.name}`);
          const result = await hook.execute(context);
          const executionTime = Date.now() - hookStart;
          const afterSnapshot = deepClone(context.envelope);
          const dataChanged = DiffEngine.hasDiff(beforeSnapshot.body, afterSnapshot.body);

          // Fire hook after event
          if (messageId) {
            this.fireEvent('onHookAfter', {
              messageId,
              hookName: hook.name,
              priority: hook.priority,
              executionTime,
              timestamp: new Date().toISOString(),
              envelopeSnapshot: afterSnapshot,
              dataChanged,
              status: result.continue ? 'success' : 'aborted',
              activityLog: Array.from(context.activityLog),
            });
          }

          // Clear activity log for next hook
          context.clearActivityLog();

          executedHooks.push(hook.name);

          if (!result.continue) {
            if (result.error) {
              lastError = new Error(result.error);
              logger.warn(`Hook ${hook.name} failed: ${result.error}`);
            }

            logger.debug(`Pipeline halted at hook ${hook.name}`);
            break;
          }
        } catch (error) {
          lastError = error instanceof Error ? error : new Error(String(error));
          logger.error(`Hook ${hook.name} threw error`, lastError);

          const executionTime = Date.now() - hookStart;
          const afterSnapshot = deepClone(context.envelope);

          // Fire hook after event with error
          if (messageId) {
            this.fireEvent('onHookAfter', {
              messageId,
              hookName: hook.name,
              priority: hook.priority,
              executionTime,
              timestamp: new Date().toISOString(),
              envelopeSnapshot: afterSnapshot,
              dataChanged: false,
              status: 'error',
              error: lastError,
              activityLog: Array.from(context.activityLog),
            });
          }

          executedHooks.push(`${hook.name} (error)`);
          break;
        }
      }

      const totalTime = Date.now() - pipelineStart;
      const finalStatus = context.isAborted ? 'aborted' : lastError ? 'failed' : 'completed';

      // Fire pipeline end event
      if (messageId) {
        this.fireEvent('onPipelineEnd', {
          messageId,
          status: finalStatus as 'completed' | 'failed' | 'aborted',
          timestamp: new Date().toISOString(),
          totalTime,
          envelopeSnapshot: deepClone(context.envelope),
        });
      }

      return {
        success: !lastError && !context.isAborted,
        aborted: context.isAborted,
        paused: false,
        executedHooks,
        error: lastError?.message,
      };
    } catch (error) {
      const pipelineError = error instanceof Error ? error : new Error(String(error));
      logger.error('Pipeline execution error', pipelineError);

      const totalTime = Date.now() - pipelineStart;

      // Fire pipeline end event even on fatal error
      if (messageId) {
        this.fireEvent('onPipelineEnd', {
          messageId,
          status: 'failed',
          timestamp: new Date().toISOString(),
          totalTime,
          envelopeSnapshot: deepClone(context.envelope),
        });
      }

      return {
        success: false,
        aborted: false,
        paused: false,
        executedHooks,
        error: pipelineError.message,
      };
    }
  }

  /**
   * Add a hook to the pipeline
   * Maintains priority order
   */
  addHook(hook: IHook): void {
    const hookArray = Array.from(this.hooks);
    hookArray.push(hook);
    hookArray.sort((a, b) => a.priority - b.priority);
    (this.hooks as IHook[]) = hookArray;
  }

  /**
   * Fire an event to all observers.
   * Observers that error are caught - errors don't affect pipeline.
   */
  private fireEvent<K extends keyof IPipelineObserver>(
    method: K,
    event: Parameters<IPipelineObserver[K]>[0],
  ): void {
    for (const observer of this.observers) {
      try {
        (observer[method] as (e: typeof event) => void)(event);
      } catch (error) {
        logger.warn(`[OutgoingPipeline] Observer ${method} error: ${error}`);
        // Observer errors must not affect pipeline
      }
    }
  }
}
